import { memo, useCallback, useMemo } from 'react'
import Table from '../../../../../components/Table'
import { IChatManagementReport, IDrillSelection } from '../models'
import {
  buildHero,
  buildKpiCards,
  drillSelection,
  FINDING_ICONS,
  FINDING_LABELS,
  findingTone,
  formatCount,
  formatHour,
  formatLocalDay,
  formatPercent,
  intentChartRows,
  isBusinessHour,
  KpiDelta,
  PALETTE,
  successTreatmentLabel,
} from '../viewModel'
import {
  CategoryDoughnutChart,
  DailyRateChart,
  DailyVolumeChart,
  HOURLY_LEGEND,
  HourlyChart,
  IntentStackChart,
  OUTCOME_LEGEND,
  OutcomeDistributionChart,
  Sparkline,
  SwatchLegend,
} from './ManagementReportCharts'
import {
  countCell,
  DrillHandler,
  evaluatedRateCell,
  makeDrillCell,
  percentCell,
  ReportSection,
  TotalLine,
} from './ReportParts'
import styles from './ChatbotManagementReport.module.scss'

const DELTA_ARROWS: Record<KpiDelta['direction'], string> = { up: '▲', down: '▼', flat: '▬', none: '•' }

export function DeltaBadge({ delta }: { delta: KpiDelta }) {
  return (
    <span className={`${styles.delta} ${styles[`delta_${delta.tone}`]}`}>
      <span aria-hidden='true'>{DELTA_ARROWS[delta.direction]}</span> {delta.text}
      {delta.direction !== 'none' ? <span className={styles.visuallyHidden}> önceki döneme göre</span> : null}
    </span>
  )
}

/** KPI kartlarından detaya inenler (spec §8.6). */
const KPI_DRILL: Record<string, IDrillSelection | undefined> = {
  noResult: drillSelection.outcome('noResult'),
  fallback: drillSelection.outcome('fallback'),
}

interface ManagementReportTabProps {
  report: IChatManagementReport
  onDrill?: DrillHandler
}

function ManagementReportTab({ report, onDrill }: ManagementReportTabProps) {
  const kpis = report.Kpis
  const hero = useMemo(() => buildHero(report), [report])
  const cards = useMemo(() => buildKpiCards(report), [report])
  const intentRowsForChart = useMemo(() => intentChartRows(report), [report])
  const businessShare = useMemo(() => {
    const business = report.Hourly.filter(row => isBusinessHour(row.Hour)).reduce((sum, row) => sum + row.Total, 0)
    return kpis.TotalTurns === 0 ? 0 : (business / kpis.TotalTurns) * 100
  }, [report, kpis.TotalTurns])

  const drillByIndex = useCallback(
    (toSelection: (index: number) => IDrillSelection | null) =>
      onDrill
        ? (index: number) => {
            const selection = toSelection(index)
            if (selection) onDrill(selection)
          }
        : undefined,
    [onDrill]
  )

  const onOutcomeSelect = useMemo(
    () => drillByIndex(index => (report.Outcomes[index] ? drillSelection.outcome(report.Outcomes[index].Code) : null)),
    [drillByIndex, report]
  )
  const onCategorySelect = useMemo(
    () =>
      drillByIndex(index =>
        report.Categories[index]
          ? drillSelection.category(report.Categories[index].Key, report.Categories[index].Label)
          : null
      ),
    [drillByIndex, report]
  )
  const onDaySelect = useMemo(
    () => drillByIndex(index => (report.Daily[index] ? drillSelection.date(report.Daily[index].Date) : null)),
    [drillByIndex, report]
  )
  const onHourSelect = useMemo(
    () => drillByIndex(index => (report.Hourly[index] ? drillSelection.hour(report.Hourly[index].Hour) : null)),
    [drillByIndex, report]
  )
  const onIntentSelect = useMemo(
    () =>
      drillByIndex(index => {
        const row = intentRowsForChart[index]
        return row && row.Intent !== 'other' ? drillSelection.intent(row.Intent, row.Label) : null
      }),
    [drillByIndex, intentRowsForChart]
  )

  const outcomeColumns = useMemo(
    () => [
      { Header: 'Yanıt Türü', accessor: 'code', Cell: makeDrillCell(onDrill, row => drillSelection.outcome(row.code)) },
      { Header: 'Anlamı', accessor: 'label' },
      { Header: 'Değerlendirme', accessor: 'category' },
      { Header: 'Adet', accessor: 'count', Cell: countCell },
      { Header: 'Pay', accessor: 'share', Cell: percentCell },
      { Header: 'Kullanıcı ne görür', accessor: 'userSees' },
      { Header: 'Başarı Sayılır?', accessor: 'treatment' },
    ],
    [onDrill]
  )
  const outcomeRows = useMemo(
    () =>
      report.Outcomes.map(row => ({
        code: row.Code,
        label: row.Label,
        category: row.Category,
        count: row.Count,
        share: row.Share,
        userSees: row.UserSees,
        treatment: successTreatmentLabel(row.SuccessTreatment),
      })),
    [report]
  )

  const categoryColumns = useMemo(
    () => [
      {
        Header: 'Kategori',
        accessor: 'label',
        Cell: makeDrillCell(onDrill, row => drillSelection.category(row.key, row.label)),
      },
      { Header: 'Açıklama', accessor: 'description' },
      { Header: 'Adet', accessor: 'count', Cell: countCell },
      { Header: 'Pay', accessor: 'share', Cell: percentCell },
      { Header: 'Başarı Sayılır?', accessor: 'treatment' },
    ],
    [onDrill]
  )
  const categoryRows = useMemo(
    () =>
      report.Categories.map(row => ({
        key: row.Key,
        label: row.Label,
        description: row.Description,
        count: row.Count,
        share: row.Share,
        treatment: successTreatmentLabel(row.SuccessTreatment),
      })),
    [report]
  )

  const dailyColumns = useMemo(
    () => [
      {
        Header: 'Gün',
        accessor: 'date',
        Cell: makeDrillCell(
          onDrill,
          row => drillSelection.date(row.date),
          value => formatLocalDay(value)
        ),
      },
      { Header: 'Soru', accessor: 'total', Cell: countCell },
      { Header: 'Başarılı (sayılan)', accessor: 'counted', Cell: countCell },
      { Header: 'Başarı %', accessor: 'successRate', Cell: evaluatedRateCell },
      { Header: 'Veri Yok %', accessor: 'noResultRate', Cell: evaluatedRateCell },
      { Header: 'Fallback %', accessor: 'fallbackRate', Cell: evaluatedRateCell },
    ],
    [onDrill]
  )
  const dailyRows = useMemo(
    () =>
      report.Daily.map(day => ({
        date: day.Date,
        total: day.Total,
        counted: day.SuccessCounted,
        successRate: day.SuccessRate,
        noResultRate: day.NoResultRate,
        fallbackRate: day.FallbackRate,
        noRate: day.Total === 0,
      })),
    [report]
  )

  const hourlyColumns = useMemo(
    () => [
      {
        Header: 'Saat',
        accessor: 'hour',
        Cell: makeDrillCell(
          onDrill,
          row => drillSelection.hour(row.hour),
          value => formatHour(value)
        ),
      },
      { Header: 'Soru', accessor: 'total', Cell: countCell },
      { Header: 'Pay', accessor: 'share', Cell: percentCell },
      { Header: 'Başarı %', accessor: 'successRate', Cell: evaluatedRateCell },
    ],
    [onDrill]
  )
  const hourlyRows = useMemo(
    () =>
      report.Hourly.map(row => ({
        hour: row.Hour,
        total: row.Total,
        share: row.Share,
        successRate: row.SuccessRate,
        noRate: row.Total === 0,
      })),
    [report]
  )

  const intentColumns = useMemo(
    () => [
      {
        Header: 'Araç',
        accessor: 'intent',
        Cell: makeDrillCell(onDrill, row => (row.intent === '—' ? null : drillSelection.intent(row.intent, row.label))),
      },
      { Header: 'İşlev', accessor: 'label' },
      { Header: 'Soru', accessor: 'total', Cell: countCell },
      { Header: 'Başarılı (sayılan)', accessor: 'counted', Cell: countCell },
      { Header: 'Veri Yok', accessor: 'noResult', Cell: countCell },
      { Header: 'Başarı %', accessor: 'successRate', Cell: evaluatedRateCell },
      { Header: 'Veri Yok %', accessor: 'noResultRate', Cell: evaluatedRateCell },
      { Header: 'Başarısız', accessor: 'failed', Cell: countCell },
    ],
    [onDrill]
  )
  const intentRows = useMemo(() => {
    const performance = report.IntentPerformance
    const rows = performance.Top.map(row => ({ ...row, intent: row.Intent, label: row.Label }))
    const withOther =
      performance.Other.Total > 0
        ? [...rows, { ...performance.Other, intent: '—', label: `Diğer ${performance.OtherIntentCount} araç` }]
        : rows
    return withOther.map(row => ({
      intent: row.intent,
      label: row.label,
      total: row.Total,
      counted: row.SuccessCounted,
      noResult: row.NoResult,
      successRate: row.SuccessRate,
      noResultRate: row.NoResultRate,
      failed: row.Failed,
      noRate: row.Total === 0,
    }))
  }, [report])

  const channelColumns = useMemo(
    () => [
      {
        Header: 'Kanal',
        accessor: 'label',
        Cell: makeDrillCell(onDrill, row => drillSelection.channel(row.key, row.label)),
      },
      { Header: 'Soru', accessor: 'total', Cell: countCell },
      { Header: 'Pay', accessor: 'share', Cell: percentCell },
      { Header: 'Başarı %', accessor: 'successRate', Cell: evaluatedRateCell },
      { Header: 'Veri Yok %', accessor: 'noResultRate', Cell: evaluatedRateCell },
      { Header: 'Fallback %', accessor: 'fallbackRate', Cell: evaluatedRateCell },
    ],
    [onDrill]
  )
  const channelRows = useMemo(
    () =>
      report.Channels.map(row => ({
        key: row.Key,
        label: row.Label,
        total: row.Total,
        share: row.Share,
        successRate: row.SuccessRate,
        noResultRate: row.NoResultRate,
        fallbackRate: row.FallbackRate,
        noRate: row.Total === 0,
      })),
    [report]
  )

  const totals = report.IntentPerformance.Totals

  return (
    <div className={styles.reportBody}>
      {onDrill ? (
        <p className={styles.drillHint}>Satırlara, grafiklere ve işaretli kartlara tıklayarak detaya inebilirsiniz.</p>
      ) : null}

      <section className={styles.hero} aria-label='Başarı oranı'>
        <div className={styles.heroMain}>
          <span className={styles.eyebrow}>Başarı oranı · yönetim tanımı</span>
          <strong className={styles.heroValue}>{hero.value}</strong>
          <span className={styles.heroCaption}>{hero.caption}</span>
          <DeltaBadge delta={hero.delta} />
        </div>
        <div className={styles.heroTrend}>
          <span className={styles.trendLabel}>Günlük başarı oranı</span>
          <Sparkline values={hero.trend} color={PALETTE.success} label='Günlük başarı oranı eğilimi' />
        </div>
      </section>

      <section className={styles.kpiGrid} aria-label='Temel göstergeler'>
        {cards.map(card => {
          const selection = onDrill ? KPI_DRILL[card.key] : undefined
          const content = (
            <>
              <span className={styles.kpiLabel}>{card.label}</span>
              <strong className={styles.kpiValue}>{card.value}</strong>
              <span className={styles.kpiCaption}>{card.caption}</span>
              <DeltaBadge delta={card.delta} />
              {card.trend ? (
                <Sparkline values={card.trend} color={PALETTE.success} label={`${card.label} eğilimi`} />
              ) : null}
            </>
          )
          return selection && onDrill ? (
            <button
              type='button'
              className={`${styles.kpiCard} ${styles.kpiButton}`}
              key={card.key}
              data-testid={`kpi-${card.key}`}
              onClick={() => onDrill(selection)}
            >
              {content}
            </button>
          ) : (
            <article className={styles.kpiCard} key={card.key} data-testid={`kpi-${card.key}`}>
              {content}
            </article>
          )
        })}
      </section>

      <ReportSection number={1} title='Yanıt Türü Dağılımı'>
        <div className={styles.sectionGrid}>
          <div className={styles.chartCard}>
            <OutcomeDistributionChart report={report} onSelect={onOutcomeSelect} />
            <SwatchLegend items={OUTCOME_LEGEND} />
          </div>
          <div className={styles.tableCard}>
            <Table columns={outcomeColumns} data={outcomeRows} />
            <TotalLine>
              Toplam {formatCount(kpis.TotalTurns)} soru · başarılı sayılan {formatCount(kpis.SuccessCountedTurns)} ·
              başarı oranı {formatPercent(kpis.SuccessRate)}
            </TotalLine>
          </div>
        </div>
      </ReportSection>

      <ReportSection number={2} title='Performans Kategorileri'>
        <div className={styles.sectionGrid}>
          <div className={styles.chartCard}>
            <CategoryDoughnutChart report={report} onSelect={onCategorySelect} />
          </div>
          <div className={styles.tableCard}>
            <Table columns={categoryColumns} data={categoryRows} />
            <TotalLine>
              Toplam {formatCount(kpis.TotalTurns)} soru · başarı oranı {formatPercent(kpis.SuccessRate)} (iptal
              edilenler paydada değil)
            </TotalLine>
          </div>
        </div>
      </ReportSection>

      <ReportSection number={3} title='Günlük Trend'>
        <div className={styles.sectionGrid}>
          <div className={styles.chartCard}>
            <p className={styles.chartTitle}>Günlük soru hacmi</p>
            <DailyVolumeChart report={report} onSelect={onDaySelect} />
            <p className={styles.chartTitle}>Günlük oranlar</p>
            <DailyRateChart report={report} onSelect={onDaySelect} />
          </div>
          <div className={styles.tableCard}>
            <Table columns={dailyColumns} data={dailyRows} />
            <TotalLine>
              Toplam {formatCount(kpis.TotalTurns)} soru · başarılı {formatCount(kpis.SuccessCountedTurns)} · başarı{' '}
              {formatPercent(kpis.SuccessRate)} · veri yok {formatPercent(kpis.NoResultRate)} · fallback{' '}
              {formatPercent(kpis.FallbackRate)}
            </TotalLine>
          </div>
        </div>
      </ReportSection>

      <ReportSection number={4} title='Saatlik Yoğunluk'>
        <div className={styles.sectionGrid}>
          <div className={styles.chartCard}>
            <HourlyChart report={report} onSelect={onHourSelect} />
            <SwatchLegend items={HOURLY_LEGEND} />
          </div>
          <div className={styles.tableCard}>
            <Table columns={hourlyColumns} data={hourlyRows} />
            <TotalLine>
              Toplam {formatCount(kpis.TotalTurns)} soru · mesai saatlerinde (09:00–17:59){' '}
              {formatPercent(businessShare)}
            </TotalLine>
          </div>
        </div>
      </ReportSection>

      <ReportSection number={5} title='Araç (Niyet) Bazında Performans'>
        <IntentStackChart report={report} onSelect={onIntentSelect} />
        <div className={styles.tableCard}>
          <Table columns={intentColumns} data={intentRows} />
          <TotalLine>
            Toplam {formatCount(totals.Total)} soru · başarılı {formatCount(totals.SuccessCounted)} · veri yok{' '}
            {formatCount(totals.NoResult)} · başarısız {formatCount(totals.Failed)} (veri yok, başarılı sayılanın
            içindedir)
          </TotalLine>
        </div>
      </ReportSection>

      <ReportSection number={6} title='Soru Giriş Kanalı'>
        <div className={styles.tableCard}>
          <Table columns={channelColumns} data={channelRows} />
          <TotalLine>Toplam {formatCount(kpis.TotalTurns)} soru · Yazılı = serbest metin + hızlı yol</TotalLine>
        </div>
      </ReportSection>

      <ReportSection number={7} title='Öne Çıkan Bulgular ve Öneriler'>
        <ul className={styles.findingList}>
          {report.Findings.map((finding, index) => {
            const tone = findingTone(finding.Kind)
            return (
              <li key={`${finding.Kind}-${index}`} className={`${styles.finding} ${styles[`finding_${tone}`]}`}>
                <span className={styles.findingBadge}>
                  <span aria-hidden='true'>{FINDING_ICONS[tone]}</span>
                  {FINDING_LABELS[finding.Kind]}
                </span>
                <p>{finding.Text}</p>
              </li>
            )
          })}
        </ul>
      </ReportSection>

      <ul className={styles.footnotes}>
        <li>Oranlar değerlendirilen sorulara (iptal edilenler hariç), paylar tüm sorulara bölünür.</li>
        <li>Gün ve saatler {report.TimeZone} saatine göredir.</li>
        <li>Oylar, seçilen aralıkta başlayan turlara verilmiş oylardır (tur zamanı).</li>
        <li>
          Chatbot Analitiği › Genel Bakış sekmesindeki fallback oranı yalnız yazılı sorulara bölündüğü için bu
          sayfadakinden farklı olabilir.
        </li>
      </ul>
    </div>
  )
}

// Kabın tarih taslağı ve detay penceresi durumu değişince ağır sekme (tablolar, grafikler) yeniden çizilmesin.
export default memo(ManagementReportTab)
